import { Node } from '@xyflow/react';

import {
  isTextNoteNode,
  textNoteNodeToConfig,
} from '@/components/diagrams/FlowChart/utils/textNoteUtils';
import { EntityDiagramVariant } from '@/modules/entityDiagram/types';
import { EntityDiagramKind } from '@/types/schema';

export interface NodeConfiguration {
  nodeID: string;
  xPosition: number;
  yPosition: number;
  type?: string;
  iconShape?: string;
  tileEmphasis?: string;
  notes?: string;
  width?: number;
  height?: number;
  fontSize?: string;
  dimensionsFinalized?: boolean;
  entityDiagramConfig?: { hideGroupContents: boolean };
}

/**
 * Extracts node configurations from FlowChart nodes for saving to the backend.
 * Only includes 'tile' type nodes, as other node types (like section labels) are derived.
 */
export function buildNodeConfigurations(nodes: Node[]): NodeConfiguration[] {
  return nodes.flatMap((node) => {
    if (isTextNoteNode(node)) {
      const cfg = textNoteNodeToConfig(node);
      return {
        nodeID: cfg.nodeID,
        xPosition: cfg.xPosition ?? 0,
        yPosition: cfg.yPosition ?? 0,
        ...(cfg.type && { type: cfg.type }),
        ...(typeof cfg.notes === 'string' && { notes: cfg.notes }),
        ...(cfg.width && { width: cfg.width }),
        ...(cfg.height && { height: cfg.height }),
      };
    }

    if (node.type !== 'tile') return [];

    const componentType = node.data.componentType;
    const iconShape = node.data.iconShape;
    const tileEmphasis = node.data.tileEmphasis;
    const fontSizeVariant = node.data.fontSizeVariant;
    const dimensionsFinalized = node.data.dimensionsFinalized;
    const notes = node.data.notes;

    const config: NodeConfiguration = {
      nodeID: node.id,
      xPosition: node.position.x,
      yPosition: node.position.y,
    };

    if (typeof componentType === 'string' && componentType) {
      config.type = componentType;
    }
    if (typeof iconShape === 'string' && iconShape) {
      config.iconShape = iconShape;
    }
    if (typeof tileEmphasis === 'string' && tileEmphasis) {
      config.tileEmphasis = tileEmphasis;
    }
    if (typeof fontSizeVariant === 'string' && fontSizeVariant) {
      config.fontSize = fontSizeVariant;
    }
    if (typeof dimensionsFinalized === 'boolean') {
      config.dimensionsFinalized = dimensionsFinalized;
    }
    if (typeof notes === 'string') {
      config.notes = notes;
    }
    if (node.data.isGroupNode) {
      config.entityDiagramConfig = {
        hideGroupContents:
          typeof node.data.hideGroupContents === 'boolean'
            ? node.data.hideGroupContents
            : false,
      };
    }

    // Persist pixel dimensions only for tiles explicitly resized via NodeResizer
    // (positive numeric data.width). Default and bulk-+/- tiles keep an
    // auto-width object and save no width, so their computed layout is preserved
    // on reload. Never persist 0 — the backend stores width/height as
    // non-nullable float64, so a 0 reads back as "unsaved".
    const dataWidth = node.data.width;
    if (typeof dataWidth === 'number' && dataWidth > 0) {
      config.width =
        typeof node.width === 'number' && node.width > 0
          ? node.width
          : dataWidth;

      const dataHeight = node.data.height;
      if (typeof node.height === 'number' && node.height > 0) {
        config.height = node.height;
      } else if (typeof dataHeight === 'number' && dataHeight > 0) {
        config.height = dataHeight;
      }
    }

    return config;
  });
}

export interface BuildCreateEntityDiagramInputParams {
  householdId: string;
  entityId: string;
  kind: EntityDiagramKind;
  nodeConfigurations: NodeConfiguration[];
  hideEntitiesListInGroups: boolean;
  entityDiagramVariant: EntityDiagramVariant;
  selectedPrimaryClientId: string;
}

export interface CreateEntityDiagramInput {
  householdID: string;
  entityID: string;
  kind: EntityDiagramKind;
  nodeConfigurations: NodeConfiguration[];
  hideEntitiesListInGroups: boolean;
  firstGrantorDeathID?: string;
}

/**
 * Builds the input for creating a new entity diagram.
 */
export function buildCreateEntityDiagramInput({
  householdId,
  entityId,
  kind,
  nodeConfigurations,
  hideEntitiesListInGroups,
  entityDiagramVariant,
  selectedPrimaryClientId,
}: BuildCreateEntityDiagramInputParams): CreateEntityDiagramInput {
  return {
    householdID: householdId,
    entityID: entityId,
    kind,
    nodeConfigurations,
    hideEntitiesListInGroups,
    ...(entityDiagramVariant === EntityDiagramVariant.Dispositions && {
      firstGrantorDeathID: selectedPrimaryClientId,
    }),
  };
}
